import { useCallback, useRef, useState, type PointerEvent, type ReactNode } from "react";
import { Mail, Trash2 } from "lucide-react";

const ACTION_WIDTH = 80;
const SWIPE_COMMIT = 68;
const AXIS_LOCK_PX = 16;
const TAP_SLOP_PX = 14;

function rubberBandOffset(dx: number): number {
  const limit = ACTION_WIDTH;
  if (dx >= 0) {
    return dx <= limit ? dx : limit + (dx - limit) * 0.22;
  }
  const abs = Math.abs(dx);
  return abs <= limit ? dx : -(limit + (abs - limit) * 0.22);
}

function swipeProgress(offset: number): { unread: number; delete: number } {
  return {
    unread: Math.min(1, Math.max(0, offset) / ACTION_WIDTH),
    delete: Math.min(1, Math.max(0, -offset) / ACTION_WIDTH),
  };
}

type Props = {
  children: ReactNode;
  active?: boolean;
  onSelect: () => void;
  onMarkUnread: () => void;
  onDelete: () => void;
  className?: string;
  convId?: number;
};

/**
 * Ligne conversation : glisser / clic enfoncé vers la droite = non lu, vers la gauche = supprimer.
 * Pointer Events : souris (desktop) et tactile (mobile).
 */
export function ChatSwipeableConversationRow({
  children,
  active = false,
  onSelect,
  onMarkUnread,
  onDelete,
  className = "",
  convId,
}: Props) {
  const startRef = useRef({ x: 0, y: 0 });
  const axisRef = useRef<"none" | "x" | "y">("none");
  const scrollGestureRef = useRef(false);
  const maxMoveRef = useRef(0);
  const offsetRef = useRef(0);
  const rowRef = useRef<HTMLDivElement>(null);
  const ignoreClickRef = useRef(false);
  const pinTouchRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const capturedRef = useRef(false);

  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const setOffsetSafe = useCallback((value: number) => {
    const banded = rubberBandOffset(value);
    const clamped = Math.max(
      -(rowRef.current?.offsetWidth ?? 420),
      Math.min(ACTION_WIDTH * 1.35, banded)
    );
    offsetRef.current = clamped;
    setOffset(clamped);
  }, []);

  const resetGesture = useCallback(() => {
    setDragging(false);
    setOffsetSafe(0);
    axisRef.current = "none";
    scrollGestureRef.current = false;
    maxMoveRef.current = 0;
  }, [setOffsetSafe]);

  const suppressClick = () => {
    ignoreClickRef.current = true;
    window.setTimeout(() => {
      ignoreClickRef.current = false;
    }, 400);
  };

  const releaseCapture = (el: HTMLElement | null, pointerId: number | null) => {
    if (el && pointerId != null && capturedRef.current) {
      try {
        el.releasePointerCapture(pointerId);
      } catch {
        // déjà relâché
      }
    }
    capturedRef.current = false;
    pointerIdRef.current = null;
  };

  const ensureCapture = (el: HTMLElement, pointerId: number) => {
    if (capturedRef.current) return;
    try {
      el.setPointerCapture(pointerId);
      capturedRef.current = true;
    } catch {
      // ignore
    }
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    pinTouchRef.current = Boolean((e.target as Element | null)?.closest?.("[data-chat-pin]"));
    if (pinTouchRef.current) return;
    pointerIdRef.current = e.pointerId;
    startRef.current = { x: e.clientX, y: e.clientY };
    axisRef.current = "none";
    scrollGestureRef.current = false;
    maxMoveRef.current = 0;
    // Souris : capture tout de suite (le drag sort souvent de la ligne).
    // Tactile : on attend le lock horizontal pour ne pas voler le scroll.
    if (e.pointerType === "mouse") {
      ensureCapture(e.currentTarget, e.pointerId);
    }
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    if (pinTouchRef.current) return;

    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    maxMoveRef.current = Math.max(maxMoveRef.current, Math.abs(dx), Math.abs(dy));

    if (axisRef.current === "none") {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;

      if (Math.abs(dy) >= Math.abs(dx)) {
        axisRef.current = "y";
        scrollGestureRef.current = true;
        return;
      }

      axisRef.current = "x";
      setDragging(true);
      ensureCapture(e.currentTarget, e.pointerId);
    }

    if (axisRef.current === "y" || scrollGestureRef.current) {
      return;
    }

    if (e.cancelable) e.preventDefault();
    setOffsetSafe(dx);
  };

  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current != null && e.pointerId !== pointerIdRef.current) return;
    const el = e.currentTarget;
    const pid = pointerIdRef.current;
    setDragging(false);

    if (pinTouchRef.current || (e.target as Element | null)?.closest?.("[data-chat-pin]")) {
      suppressClick();
      pinTouchRef.current = false;
      releaseCapture(el, pid);
      resetGesture();
      return;
    }

    suppressClick();

    if (scrollGestureRef.current || axisRef.current === "y") {
      releaseCapture(el, pid);
      resetGesture();
      return;
    }

    const current = offsetRef.current;
    if (current >= SWIPE_COMMIT) {
      setOffsetSafe(ACTION_WIDTH * 0.55);
      onMarkUnread();
      try {
        if (navigator.vibrate) navigator.vibrate(10);
      } catch {
        // ignore
      }
      releaseCapture(el, pid);
      window.setTimeout(() => resetGesture(), 180);
      return;
    }
    if (current <= -SWIPE_COMMIT) {
      try {
        if (navigator.vibrate) navigator.vibrate(10);
      } catch {
        // ignore
      }
      releaseCapture(el, pid);
      resetGesture();
      onDelete();
      return;
    }

    if (axisRef.current === "x") {
      releaseCapture(el, pid);
      resetGesture();
      return;
    }

    if (maxMoveRef.current <= TAP_SLOP_PX) {
      onSelect();
    }
    releaseCapture(el, pid);
    resetGesture();
  };

  const onPointerCancel = (e: PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current != null && e.pointerId !== pointerIdRef.current) return;
    releaseCapture(e.currentTarget, pointerIdRef.current);
    resetGesture();
  };

  const progress = swipeProgress(offset);
  const rowTransition = dragging
    ? undefined
    : "transform 0.38s cubic-bezier(0.32, 0.72, 0, 1)";

  return (
    <div
      ref={rowRef}
      data-chat-conv-id={convId}
      className="chat-swipe-row relative overflow-hidden"
    >
      <div
        className="chat-swipe-action chat-swipe-action--unread absolute inset-y-0 left-0 flex w-[5rem] items-center justify-center gap-1.5"
        style={{
          opacity: 0.35 + progress.unread * 0.65,
          transform: `scale(${0.88 + progress.unread * 0.12})`,
        }}
        aria-hidden
      >
        <Mail
          className="size-4 transition-transform duration-150"
          style={{ transform: `scale(${0.9 + progress.unread * 0.2})` }}
        />
        <span className="text-[10px] font-semibold">Non lu</span>
      </div>
      <div
        className="chat-swipe-action chat-swipe-action--delete absolute inset-y-0 right-0 flex w-[5rem] items-center justify-center gap-1.5"
        style={{
          opacity: 0.3 + progress.delete * 0.7,
          transform: `scale(${0.88 + progress.delete * 0.12})`,
        }}
        aria-hidden
      >
        <Trash2
          className="size-4 transition-transform duration-150"
          style={{ transform: `scale(${0.9 + progress.delete * 0.25})` }}
        />
        <span className="text-[10px] font-semibold">Suppr.</span>
      </div>

      <div
        role="button"
        tabIndex={0}
        className={`chat-swipe-front relative flex w-full min-w-0 items-start gap-3 bg-[var(--chat-surface)] px-3 py-2.5 text-left outline-none transition-colors hover:bg-[var(--chat-surface-elevated)] ${
          active ? "bg-[var(--chat-accent-soft)] hover:bg-[var(--chat-accent-soft)]" : ""
        } ${dragging && Math.abs(offset) > 12 ? "chat-swipe-front--active" : ""} ${className}`}
        style={{
          transform: offset !== 0 ? `translateX(${offset}px)` : undefined,
          transition: rowTransition,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onDragStart={(e) => e.preventDefault()}
        onClick={() => {
          if (ignoreClickRef.current) return;
          onSelect();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        {children}
      </div>
    </div>
  );
}
